import { useMemo, useState } from "react";
import { matchDrawings, prepareImport, describeImport, sameEnvironment } from "../../utils/takeoffFile";

// Importing a takeoff file: which of the file's drawings is which of this revision's, what will be replaced, and
// what will be left out — all shown BEFORE anything is changed. Nothing is guessed: a drawing that cannot be matched
// to exactly one sheet is left to the person (or skipped), and rows on a skipped drawing are not imported.

const sheetLabel = (s) => [s.SheetNumber, s.SheetName].filter(Boolean).join(" — ") || s.FileName || `Drawing ${s.SheetID}`;
const drawingLabel = (d) => [d.number, d.name].filter(Boolean).join(" — ") || d.fileName || `Drawing ${d.sheetId}`;
const BY = { hash: "same file", name: "same name" };

const btn = (primary, disabled) => ({ fontSize: 12, fontWeight: 700, padding: "6px 14px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, border: `1px solid ${primary ? "#2563eb" : "var(--cs-border)"}`, background: primary ? "#2563eb" : "var(--surface)", color: primary ? "#fff" : "var(--text)" });

export default function TakeoffImportDialog({ file, warnings = [], fileName = "", archive = null, onCreateSheets = null, targetSheets, targetRevisionId, targetRoomCount = 0, targetConditionCount = 0, isLocked, host, onImport, onCancel }) {
  const matches = useMemo(() => matchDrawings(file.drawings, targetSheets), [file, targetSheets]);
  // A drawing the revision does not have, whose file is in the archive, defaults to being added as a new drawing.
  const canAdd = (d) => !!(archive && onCreateSheets && archive.get(d.sheetId) && !archive.get(d.sheetId).tooLarge);
  const [mapping, setMapping] = useState(() => Object.fromEntries(matches.map(m => [m.drawing.sheetId, m.targetSheetId ?? (canAdd(m.drawing) ? "new" : null)])));
  const [confirmHost, setConfirmHost] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const { document, report } = useMemo(() => prepareImport({ file, mapping, targetRevisionId }), [file, mapping, targetRevisionId]);
  const lines = describeImport(report, { targetRoomCount, targetConditionCount });
  const foreign = !sameEnvironment(file, host);
  const nothing = report.rooms + report.conditions === 0;
  const blocked = isLocked || busy || nothing || (foreign && !confirmHost);

  // a target sheet can be picked by only one drawing
  const taken = (exceptDrawingId) => new Set(Object.entries(mapping).filter(([id, v]) => v != null && v !== "new" && String(id) !== String(exceptDrawingId)).map(([, v]) => v));
  const toAdd = matches.filter(m => mapping[m.drawing.sheetId] === "new");

  async function run() {
    setBusy(true); setError("");
    let added = 0;
    try {
      let finalDoc = document;
      if (toAdd.length) {
        // create the missing drawings first, then prepare the document against their real ids
        const made = await onCreateSheets(toAdd.map(m => ({ drawing: m.drawing, archive: archive.get(m.drawing.sheetId) })));
        added = Object.keys(made).length;
        const mapped = { ...mapping };
        for (const m of toAdd) mapped[m.drawing.sheetId] = made[m.drawing.sheetId] ?? null;
        finalDoc = prepareImport({ file, mapping: mapped, targetRevisionId }).document;
      }
      await onImport({ document: finalDoc, name: `Imported ${fileName || "takeoff file"}`.slice(0, 200) });
    } catch (e) {
      setError((e.message || "The import failed.") + (added ? ` (${added} drawing${added === 1 ? " was" : "s were"} already added to this revision; the takeoff was not replaced.)` : ""));
      setBusy(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 950, background: "rgba(15,23,42,0.5)", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "6vh" }}>
      <div role="dialog" aria-label="Import takeoff file" style={{ width: "min(760px, 94vw)", maxHeight: "86vh", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 10, boxShadow: "0 16px 48px rgba(0,0,0,0.4)", padding: "14px 18px", fontSize: 13 }}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 2 }}>Import takeoff file</div>
        <div style={{ color: "var(--text3)", fontSize: 12, marginBottom: 10 }}>
          {fileName ? `${fileName} · ` : ""}from {file.source?.project || "an estimate"} {file.source?.revision || ""}{file.exportedAt ? ` · exported ${new Date(file.exportedAt).toLocaleString()}` : ""}{file.exportedBy ? ` by ${file.exportedBy}` : ""}
        </div>

        {isLocked && <div role="alert" style={{ padding: "6px 10px", border: "1px solid #dc2626", borderRadius: 6, color: "#dc2626", fontWeight: 700, marginBottom: 8 }}>This revision is locked — a takeoff cannot be imported into it.</div>}
        {foreign && (
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 10px", border: "1px solid #b45309", borderRadius: 6, background: "rgba(217,119,6,0.1)", color: "#92400e", marginBottom: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={confirmHost} onChange={e => setConfirmHost(e.target.checked)} style={{ marginTop: 3 }} />
            <span><b>This file was made on a different site ({file.source.host}).</b> Room templates, classifications and conditions are matched by id, so on another site they may point at different catalog items. I understand, import anyway.</span>
          </label>
        )}
        {warnings.map((w, i) => <div key={i} style={{ color: "#92400e", fontSize: 12, marginBottom: 4 }}>{w}</div>)}

        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text3)", margin: "6px 0 4px" }}>Drawings</div>
        {matches.length === 0 ? (
          <div style={{ color: "var(--text3)", marginBottom: 6 }}>The file lists no drawings.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
            <thead><tr style={{ textAlign: "left", fontSize: 11, color: "var(--text3)" }}><th style={{ padding: "3px 6px" }}>In the file</th><th style={{ padding: "3px 6px" }}>Goes onto</th><th style={{ padding: "3px 6px" }}>Matched</th></tr></thead>
            <tbody>
              {matches.map(m => {
                const id = m.drawing.sheetId;
                const chosen = mapping[id] ?? "";
                const by = chosen === "new" ? "new, from the archive" : m.targetSheetId != null && chosen === m.targetSheetId ? BY[m.by] : (chosen === "" ? "skipped" : "chosen by you");
                const used = taken(id);
                return (
                  <tr key={id} style={{ borderTop: "1px solid var(--cs-border)" }}>
                    <td style={{ padding: "4px 6px" }}>{drawingLabel(m.drawing)}{m.drawing.fileName && <div style={{ fontSize: 11, color: "var(--text3)" }}>{m.drawing.fileName}</div>}</td>
                    <td style={{ padding: "4px 6px" }}>
                      <select value={chosen} aria-label={`Target for ${drawingLabel(m.drawing)}`} onChange={e => setMapping(prev => ({ ...prev, [id]: e.target.value === "" ? null : e.target.value === "new" ? "new" : +e.target.value }))} style={{ fontSize: 12, maxWidth: 280 }}>
                        <option value="">Skip this drawing</option>
                        {canAdd(m.drawing) && <option value="new">Add as a new drawing (from the archive)</option>}
                        {targetSheets.map(s => <option key={s.SheetID} value={s.SheetID} disabled={used.has(s.SheetID)}>{sheetLabel(s)}{used.has(s.SheetID) ? " (already used)" : ""}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "4px 6px", color: chosen === "" ? "#b45309" : "#15803d", fontWeight: 600 }}>{by}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text3)", margin: "6px 0 4px" }}>What will happen</div>
        <ul style={{ margin: "0 0 8px", paddingLeft: 18, color: "var(--text2)" }}>
          {toAdd.length > 0 && <li style={{ marginBottom: 2 }}>{toAdd.length} drawing{toAdd.length === 1 ? "" : "s"} will be added to this revision from the archive.</li>}
          {lines.map((l, i) => <li key={i} style={{ marginBottom: 2 }}>{l}</li>)}
        </ul>
        {nothing && <div style={{ color: "#b45309", marginBottom: 8 }}>Nothing would be imported — map at least one drawing.</div>}
        {error && <div role="alert" style={{ color: "#dc2626", fontWeight: 700, marginBottom: 8 }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} disabled={busy} style={btn(false, busy)}>Cancel</button>
          <button onClick={run} disabled={blocked} style={btn(true, blocked)}>{busy ? "Importing…" : "Replace this takeoff with the file"}</button>
        </div>
      </div>
    </div>
  );
}
